// src/screens/admin/CotizadorPrecios.jsx
// "Precios de referencia" (solo admin): cuánto suele costar el vuelo a cada
// destino, con lo que ya se cotizó. Es una guía aproximada, no un precio.
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { toast } from '../../components/UI';
import { getAjustes, listarHorarios, importarHistorial, hoyIso } from '../../services/cotizadorService';
import { fmtBRL, fmtFecha } from '../../services/cotizadorCalc';
import {
  agruparPorDestino, resumenDestino, registrosDeCSV, CUBETAS, DIAS_SIN_DATOS,
} from '../../services/cotizadorHistorial';
import { cargarRegistros } from './cotizadorRegistros';
import { inputStyle, cardStyle, btnChico } from './CotizadorUI';

const R$ = (v) => (v === null || v === undefined ? '—' : fmtBRL(v, 0));

function Tendencia({ valor }) {
  if (valor === null) return <span style={{ color: 'var(--muted)' }}>Tendencia: sin datos suficientes</span>;
  const pct = Math.round(valor * 100);
  const color = pct > 0 ? 'var(--error)' : pct < 0 ? 'var(--success)' : 'var(--muted)';
  return (
    <span style={{ color: 'var(--muted)' }}>
      Tendencia (7 días vs. 7 anteriores): <b style={{ color }}>{pct > 0 ? '+' : ''}{pct}%</b>
    </span>
  );
}

function Destino({ grupo, hoy, ventana }) {
  const [abierto, setAbierto] = useState(false);
  const res = useMemo(() => resumenDestino(grupo.registros, hoy, ventana), [grupo, hoy, ventana]);
  const viejo = res.ultima && res.ultima.edad > DIAS_SIN_DATOS;
  return (
    <div style={{ ...cardStyle, padding: 12, opacity: viejo ? 0.75 : 1 }}>
      <div onClick={() => setAbierto(!abierto)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
        <div style={{ minWidth: 0 }}>
          <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--white)' }}>{grupo.nombre}</p>
          <p style={{ fontSize: 11, color: 'var(--muted)' }}>
            {res.total} {res.total === 1 ? 'cotización' : 'cotizaciones'} · última {res.ultima ? fmtFecha(res.ultima.fechaCot) : '—'}
          </p>
        </div>
        <span style={{ fontSize: 13, fontWeight: 800, color: viejo ? 'var(--muted)' : 'var(--orange)', whiteSpace: 'nowrap', textAlign: 'right' }}>
          {res.ventana.n > 0 && !viejo ? `R$ ${R$(res.ventana.min)} – ${R$(res.ventana.max)}` : 'Sin datos recientes'}
        </span>
      </div>

      {abierto && (
        <div style={{ marginTop: 10 }}>
          <p style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>Costo del vuelo, últimos {ventana} días</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 0.5fr 1fr 1fr 1fr', gap: 4, fontSize: 10, fontWeight: 700, color: 'var(--muted)', paddingBottom: 4 }}>
            <span /><span style={{ textAlign: 'right' }}>N</span><span style={{ textAlign: 'right' }}>MÍN</span><span style={{ textAlign: 'right' }}>MEDIANA</span><span style={{ textAlign: 'right' }}>MÁX</span>
          </div>
          {CUBETAS.filter((c) => c.id !== 'sin' || res.porCubeta.sin.n > 0).map((c) => {
            const e = res.porCubeta[c.id];
            return (
              <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '1.3fr 0.5fr 1fr 1fr 1fr', gap: 4, fontSize: 13, padding: '3px 0', color: e.n ? 'var(--white)' : 'var(--muted)' }}>
                <span>{c.label}</span>
                <span style={{ textAlign: 'right' }}>{e.n}</span>
                <span style={{ textAlign: 'right' }}>{R$(e.min)}</span>
                <span style={{ textAlign: 'right', fontWeight: 700 }}>{R$(e.mediana)}</span>
                <span style={{ textAlign: 'right' }}>{R$(e.max)}</span>
              </div>
            );
          })}
          <p style={{ fontSize: 12, marginTop: 8 }}><Tendencia valor={res.tendencia} /></p>

          <p className="section-label" style={{ marginTop: 12 }}>ÚLTIMAS 5</p>
          {res.ultimas.map((u, i) => (
            <div key={i} style={{ borderTop: i ? '1px solid var(--navy-border)' : 'none', padding: '6px 0', fontSize: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--white)', fontWeight: 700 }}>
                <span>Cotizado {fmtFecha(u.fechaCot)}</span><span>R$ {fmtBRL(u.costo, 0)}</span>
              </div>
              <p style={{ color: 'var(--muted)', marginTop: 2 }}>
                Vuelo {fmtFecha(u.fechaVuelo) || '—'}{u.anticipacion !== null ? ` (${u.anticipacion} días antes)` : ''}
                {u.aerolinea ? ` · ${u.aerolinea}` : ''}{u.fuente ? ` · ${u.fuente}` : ''}
                {u.escalas !== null && u.escalas !== undefined ? ` · ${u.escalas === 0 ? 'directo' : `${u.escalas} esc.`}` : ''}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CotizadorPrecios() {
  const [datos, setDatos] = useState(null);
  const [ventana, setVentana] = useState(21);
  const [busqueda, setBusqueda] = useState('');
  const [importando, setImportando] = useState(false);
  const archivo = useRef(null);
  const hoy = hoyIso();

  const cargar = async () => {
    try {
      const [aj, hor] = await Promise.all([getAjustes(), listarHorarios()]);
      setVentana(aj.ventana_historial_dias || 21);
      setDatos(await cargarRegistros(aj, hor));
    } catch { toast('No se pudo cargar el historial.', 'error'); setDatos([]); }
  };
  useEffect(() => { cargar(); }, []);

  const grupos = useMemo(() => {
    if (!datos) return [];
    const q = busqueda.trim().toLowerCase();
    return agruparPorDestino(datos)
      .map((g) => ({ ...g, ultima: Math.max(...g.registros.map((r) => new Date(r.fechaCot).getTime() || 0)) }))
      .filter((g) => !q || g.nombre.toLowerCase().includes(q) || g.clave.includes(q))
      .sort((a, b) => b.ultima - a.ultima);
  }, [datos, busqueda]);

  const importar = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setImportando(true);
    try {
      const { registros, descartadas } = registrosDeCSV(await f.text());
      if (!registros.length) { toast('El archivo no tiene filas válidas.', 'error'); return; }
      const { nuevas, repetidas } = await importarHistorial(registros);
      toast(`Importadas ${nuevas}${repetidas ? ` · ${repetidas} ya estaban` : ''}${descartadas ? ` · ${descartadas} sin leer` : ''} ✅`);
      await cargar();
    } catch (err) {
      toast(err?.message?.startsWith('Faltan') ? err.message : 'No se pudo importar el CSV.', 'error');
    } finally { setImportando(false); }
  };

  return (
    <div>
      <p style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5, marginBottom: 12 }}>
        Guía aproximada con lo que ya cotizaste: el precio real cambia con la fecha del vuelo y con lo cerca que esté.
        Se cuentan los últimos {ventana} días (se cambia en Cotizador → Ajustes).
      </p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar destino…" style={{ ...inputStyle, flex: 1 }} />
        <button onClick={() => archivo.current?.click()} disabled={importando} style={{ ...btnChico, color: 'var(--orange)', borderColor: 'var(--orange)', whiteSpace: 'nowrap' }}>
          {importando ? '…' : 'Importar CSV'}
        </button>
        <input ref={archivo} type="file" accept=".csv,text/csv" onChange={importar} style={{ display: 'none' }} />
      </div>
      {datos === null && <p style={{ color: 'var(--muted)', textAlign: 'center', fontSize: 13 }}>Cargando…</p>}
      {datos && grupos.length === 0 && (
        <p style={{ color: 'var(--muted)', textAlign: 'center', fontSize: 13 }}>
          {busqueda ? 'Ningún destino con ese nombre.' : 'Todavía no hay datos. Escribe la ciudad en tus opciones de vuelo o importa el CSV.'}
        </p>
      )}
      {grupos.map((g) => <Destino key={g.clave} grupo={g} hoy={hoy} ventana={ventana} />)}
    </div>
  );
}
